import type { DocumentationTabTypes } from '../components/documentation/tabs/types';
import type { DocumentationPageType } from '../components/documentation/types';
import type { SnowflakeParentType } from '@server/snowflake-documentation';
import { telemetry } from '../telemetry';

interface StoredTabsByOrg<T> {
  [crmOrgId: string]: T;
}

const loadTabsForOrg = <T>(storageKey: string, crmOrgId: string): T | null => {
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return null;
    const allOrgs: StoredTabsByOrg<T> = JSON.parse(stored);
    return allOrgs[crmOrgId] ?? null;
  } catch (error) {
    telemetry.captureError(error);
    return null;
  }
};

const saveTabsForOrg = <T>(storageKey: string, crmOrgId: string, data: T): void => {
  try {
    const stored = localStorage.getItem(storageKey);
    let allOrgs: StoredTabsByOrg<T> = {};

    if (stored) {
      allOrgs = JSON.parse(stored);
    }

    allOrgs[crmOrgId] = data;
    localStorage.setItem(storageKey, JSON.stringify(allOrgs));
  } catch (error) {
    telemetry.captureError(error);
  }
};

// --- Salesforce (metadata) tabs ---

const METADATA_TABS_STORAGE_KEY = 'documentation_metadata_tabs';

export interface StoredMetadataTabsPerOrg {
  [DocumentationPageType.metadataTypes]: DocumentationTabTypes[];
}

export const loadMetadataTabs = (crmOrgId: string) =>
  loadTabsForOrg<StoredMetadataTabsPerOrg>(METADATA_TABS_STORAGE_KEY, crmOrgId);

export const saveMetadataTabs = (crmOrgId: string, tabs: StoredMetadataTabsPerOrg) =>
  saveTabsForOrg(METADATA_TABS_STORAGE_KEY, crmOrgId, tabs);

export const getUserSelectedTabs = (tabs: DocumentationTabTypes[]) =>
  tabs.filter((tab) => !tab.isNotRemovable);

export const mergeWithDefaultTabs = (
  defaultTabs: DocumentationTabTypes[],
  userTabs: DocumentationTabTypes[],
) => [...defaultTabs, ...userTabs];

// --- Snowflake tabs ---

const SNOWFLAKE_TABS_STORAGE_KEY = 'snowflake_documentation_tabs';

export const loadSnowflakeTabs = (crmOrgId: string) =>
  loadTabsForOrg<SnowflakeParentType[]>(SNOWFLAKE_TABS_STORAGE_KEY, crmOrgId);

export const saveSnowflakeTabs = (crmOrgId: string, tabs: SnowflakeParentType[]) =>
  saveTabsForOrg(SNOWFLAKE_TABS_STORAGE_KEY, crmOrgId, tabs);
